import { CSSProperties, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import { useStageGuard } from "../useStageGuard";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import eligStyles from "../index.module.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-11-05" -> "5 Nov 26" (same format as the app's Repayment Schedule)
const formatDate = (value: any) => {
  const [y, m, d] = String(value || "").slice(0, 10).split("-");
  return y && m && d ? `${Number(d)} ${MONTHS[Number(m) - 1]} ${y.slice(2)}` : "—";
};

const formatMoney = (value: any) =>
  value != null ? `₹${Math.round(Number(value)).toLocaleString("en-IN")}` : "—";

const COLUMNS = ["No.", "Date", "EMI", "Principle", "Interest"];

const gridRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "0.5fr 1fr 1fr 1fr 1fr",
  gap: "0.25rem",
  alignItems: "center",
  textAlign: "center",
  padding: "0.7rem 0.6rem",
};

function EligibilityRepaymentSchedule() {
  const navigate = useNavigate();
  const userData = useEligibilityData();

  useStageGuard(userData?.userId, userData?.applicationId, 8);

  const [emis, setEmis] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = JSON.parse(sessionStorage.getItem("eligibility_data") || "{}");
    const appId = userData?.applicationId || saved?.applicationId;
    if (!appId) {
      setError("Unable to load repayment schedule.");
      return;
    }

    const token = sessionStorage.getItem("eligibility_auth_token");

    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/emi-schedule?applicationId=${appId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        const list: any[] = Array.isArray(json?.data) ? json.data : [];
        if (!list.length) throw new Error("No EMI schedule");
        // Same as the app: only regular EMIs, in order
        setEmis(list.filter((e) => !e.advanceEmi).sort((a, b) => a.emiNo - b.emiNo));
      } catch {
        setError("Unable to load repayment schedule.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userData?.applicationId]);

  return (
    <div className={eligStyles.body}>
      <div className={eligStyles.backdrop}>
        <Navbar />
        <div className={eligStyles.main}>
          <div className={eligStyles.cardWrapper}>
            <div className={eligStyles.container}>
              <div className={eligStyles.cardHeader}>
                <div style={{
                  width: "2.4rem", height: "2.4rem", borderRadius: "50%",
                  background: "#F4E5DD", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="16" rx="2" fill="#d32028" fillOpacity="0.15" stroke="#d32028" strokeWidth="1.5" />
                    <path d="M3 10H21" stroke="#d32028" strokeWidth="1.5" />
                    <path d="M8 3V7M16 3V7" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <p className={eligStyles.cardTitle}>Repayment Schedule</p>
              </div>

              {loading && <Loader />}

              {!loading && emis.length > 0 && (
                <div style={{ border: "1px solid #ececec", borderRadius: "12px", overflow: "hidden" }}>
                  <div style={{ ...gridRow, background: "#fdecea", borderBottom: "1px solid #f0f0f0" }}>
                    {COLUMNS.map((col) => (
                      <span key={col} style={{ fontSize: "0.72rem", color: "#d32028", fontWeight: 800 }}>{col}</span>
                    ))}
                  </div>
                  {emis.map((emi, index) => (
                    <div
                      key={emi.emiNo}
                      style={{
                        ...gridRow,
                        borderBottom: index === emis.length - 1 ? "none" : "1px solid #f0f0f0",
                        background: index % 2 === 1 ? "#fcfbfb" : "#fff",
                      }}
                    >
                      <span style={{ fontSize: "0.75rem", color: "#666" }}>{emi.emiNo}</span>
                      <span style={{ fontSize: "0.75rem", color: "#1a1a2e", fontWeight: 600 }}>{formatDate(emi.dueDate)}</span>
                      <span style={{ fontSize: "0.75rem", color: "#1a1a2e", fontWeight: 700 }}>{formatMoney(emi.emiAmount)}</span>
                      <span style={{ fontSize: "0.75rem", color: "#1a1a2e" }}>{formatMoney(emi.principle)}</span>
                      <span style={{ fontSize: "0.75rem", color: "#1a1a2e" }}>{formatMoney(emi.interest)}</span>
                    </div>
                  ))}
                </div>
              )}

              {!loading && error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem" }}>{error}</p>
              )}
            </div>

            {/* ── Continue ── */}
            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center", gap: "0.6rem", justifyContent: "center" }}>
              <button
                onClick={() => navigate("/eligibility/sanction-letter", { state: { data: userData } })}
                style={{
                  width: "70%",
                  padding: "0.85rem 1.5rem",
                  background: "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: "pointer",
                  letterSpacing: "0.3px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/sanction-letter", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
            <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityRepaymentSchedule;
